import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import { withFormik } from 'formik';
import { withRouter } from 'react-router-dom';
import * as Yup from 'yup';
import { injectIntl } from 'react-intl';
import { requestAsync } from '../../queries/actions';
import { sendTransactionRequest, upgradeAddOnSubscriptions } from '../../queries/subscription';
import { translate } from '../../components/Translate';
import { selectClient, selectSearchObj } from '../../global-selectors';
import { selectUserData } from '../../queries/selectors/user';
import { selectAllSubPaymentMethodsConfig, selectCurrentPaymentMethod } from '../../queries/selectors/payment';
import { getSelectedFreePassProduct, selectProducts } from '../../queries/selectors/products';
import { FreePassForm } from '../FreePassForm';
import { ProductType } from '../../enums';
import { VerifyFreePassRequest } from '../../queries/coupons';
import { selectAddOnSubscriptions } from '../../queries/selectors/bootstrap';
import { selectShoppingCartConfig } from '../../queries/selectors/shoppingCart';
import { setCurrentSubPaymentMethod } from '../../queries/actions/payment';
import withCaptcha from '../../containers/withCaptcha';

const discountCodeValidations = ({ intl }) => Yup.object().shape({
  freePassCode: Yup.string()
    .min(1, translate(intl, 'FreePassForm.form.freePassCode.min'))
    .max(17, translate(intl, 'FreePassForm.form.freePassCode.max'))
    .trim(translate(intl, 'FreePassForm.form.freePassCode.trim'))
    .strict(true)
    .required(translate(intl, 'FreePassForm.form.freePassCode.required'))
});

const handleFreePassRequest = async (productsToSend, props, values) => {
  if (!productsToSend) {
    return null;
  }
  if (productsToSend.every(product => product.productType === ProductType.ADD_ON)) {
    return props.upgradeAddOnSubscriptions({
      products: props.products,
      addOnsSubscriptions: props.addOnsSubscriptions,
      methodId: 'freepass',
      paymentInfo: {
        purchaseAddOns: productsToSend.map(product => product.productID),
        couponCode: values.freePassCode.toUpperCase(),
        country: props.user.country
      }
    })
  }
  return props.sendTransaction({
    transactionType: 'SUBSCRIPTION',
    paymentMethod: 'freepass',
    data: {
      productsIds: productsToSend.map(product => product.productID),
      couponCode: values.freePassCode,
      country: props.user.country
    }
   })
}

const withFormikEnhancement = withFormik({
  // Initial state
  mapPropsToStatus: () => ({}),
  mapPropsToValues: () => ({
    freePassCode: '',
  }),
  validationSchema: discountCodeValidations,
  handleSubmit: async (values, { setStatus, setSubmitting, props }) => {
    try {
         setSubmitting(true);
         props.setIsSubmitFreeCode(true);
        let captchaToken = props.captchaToken;
        
        if (props.isCaptchaEnabled) {
          if (!captchaToken) {
            captchaToken = await props.executeCaptcha();
          }
          if (!captchaToken) {
            return;
          }
        }
         let productsToSend = props.selectedFreePassProduct ? [props.selectedFreePassProduct] : [];
         const verifyResponse = await props.VerifyFreePassRequest({
          code: values.freePassCode.toUpperCase(),
          client: props.client,
          ...(props.isCaptchaEnabled && { captchaToken }),
          skipProductCouponMatch: true,
          ...(props?.shoppingCart?.productsToBuy?.length && { shoppingCart: props.shoppingCart }),
          products: props.products
        });
        if ((verifyResponse && verifyResponse.status < 200) || (verifyResponse && verifyResponse.status >= 300)) {
         return setStatus({
           apiErrorCode: translate(props.intl, `ERRORS.${verifyResponse.body && verifyResponse.body.code}.freepass.title`, {
             defaultMessage: translate(props.intl, 'ERRORS.FreePass.title')
           })
         });
        }
        if (Object.entries(verifyResponse?.entities?.couponApply || {}).length > 0) {
          const currentSubPaymentMethod = props.subPaymentMethods.find(sub => sub.countryCode.toLowerCase() === props.searchObj.country.toLowerCase() && sub.active);
          props.setCurrentSubPaymentMethod(currentSubPaymentMethod);
          return props.history.push(`methods/${currentSubPaymentMethod.paymentMethod}/${currentSubPaymentMethod.type}${props.history.location.search}`);
        }
        
        if (productsToSend.length === 0) {
          productsToSend = verifyResponse.body?.associatedProducts
        }
         const response = await handleFreePassRequest(productsToSend, props, values);
         if ((response && response.status < 200) || (response && response.status >= 300)) {
          if (props.isCaptchaEnabled) props.resetCaptcha();
          return setStatus({
            apiErrorCode: translate(props.intl, `ERRORS.${response.body && response.body.code}.title`, {
              defaultMessage: translate(props.intl, 'ERRORS.FreePass.title')
            })
          });
        }
         response.body.productsName = [];
         productsToSend.forEach(product => {
          if (product !== undefined) {
            const productName = product.name || '-';
            response.body.productsName.push(productName);
          }
         })
         props.setFreePassResponse(response);
         props.resetCaptcha();
       } finally {
         props.setIsSubmitFreeCode(false);
         setSubmitting(false);
       }
  },
});

const mapStateToProps = (state) => ({
  client: selectClient(state),
  user: selectUserData(state),
  paymentMethod: selectCurrentPaymentMethod(state),
  products: selectProducts(state),
  addOnSubscriptions: selectAddOnSubscriptions(state),
  selectedFreePassProduct: getSelectedFreePassProduct(state),
  shoppingCart: selectShoppingCartConfig(state),
  subPaymentMethods: selectAllSubPaymentMethodsConfig(state),
  searchObj: selectSearchObj(state)
});

const mapDispatchToProps = (
  (dispatch) => bindActionCreators({
    sendTransaction: (data) => requestAsync(sendTransactionRequest, data),
    setCurrentSubPaymentMethod: payload => dispatch(setCurrentSubPaymentMethod(payload)),
    upgradeAddOnSubscriptions: (data) => requestAsync(upgradeAddOnSubscriptions, data),
    VerifyFreePassRequest: (data) => requestAsync(VerifyFreePassRequest, data),
  }, dispatch)
);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withCaptcha,
  withRouter,
  withFormikEnhancement
)(FreePassForm);
